import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Check, AlertCircle, Lock, CreditCard } from 'lucide-react';
import api from '../utils/api';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planType = searchParams.get('plan') || 'personal';
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [trialDays, setTrialDays] = useState(15);
  
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    signatureUrl: '',
    firstName: '',
    lastName: '',
    email: '',
    agreeToTerms: false
  });

  useEffect(() => {
    fetchPlanDetails();
    api.get('/trial/config')
      .then((res) => setTrialDays(res.data.trial_days))
      .catch(() => {});
  }, [planType]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stripe/plans');
      setPlanDetails(response.data[planType]);
    } catch (error) {
      console.error('Failed to fetch plan details:', error);
      setError('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSignatureUrl = (companyName) => {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleCompanyNameChange = (e) => {
    const companyName = e.target.value;
    setFormData(prev => ({
      ...prev,
      companyName,
      signatureUrl: generateSignatureUrl(companyName)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }
    
    setError(null);
    setSubmitting(true);
    
    try {
      const response = await api.post('/stripe/create-checkout-session', {
        plan: planType,
        company_name: formData.companyName,
        website: formData.website || null,
        signature_url: formData.signatureUrl,
        admin_first_name: formData.firstName,
        admin_last_name: formData.lastName,
        admin_email: formData.email,
        success_url: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/checkout?plan=${planType}`
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      setError(error.response?.data?.detail || 'Failed to create checkout session');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                alt="SlickSignature"
                className="h-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                }}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              className="text-white hover:bg-gray-800"
            >
              ← Back to Pricing
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Form */}
          <div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Start Your Free Trial</h1>
              <p className="text-gray-600">
                Get {trialDays} days free. A credit card is required to start your trial.
              </p>
            </div>

            {error && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Information</CardTitle>
                  <CardDescription>Tell us about your business</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleCompanyNameChange}
                      required
                      placeholder="Acme Inc."
                    />
                  </div>

                  <div>
                    <Label htmlFor="website">Website (Optional)</Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="signatureUrl">Signature URL *</Label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">slicksignature.com/signature/</span>
                      <Input
                        id="signatureUrl"
                        name="signatureUrl"
                        value={formData.signatureUrl}
                        onChange={handleInputChange}
                        required
                        placeholder="company-name"
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Your team will access their signatures at this URL
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Admin Account</CardTitle>
                  <CardDescription>This will be your login account</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="admin@company.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      You'll use this email to sign in
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CreditCard className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">
                        Payment Details on Next Page
                      </p>
                      <p className="text-xs text-blue-700">
                        After clicking continue, you'll be securely redirected to Stripe to enter your payment information. Your trial starts immediately, and you won't be charged for {trialDays} days.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="mt-1"
                  required
                />
                <label htmlFor="agreeToTerms" className="text-sm text-gray-600">
                  I agree to the{' '}
                  <a href="#" className="text-primary hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-6 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5 mr-2" />
                    Continue to Payment
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                <Lock className="h-3 w-3 inline mr-1" />
                Secure checkout powered by Stripe
              </p>
            </form>
          </div>

          {/* Right Side - Order Summary */}
          <div className="lg:sticky lg:top-8 h-fit">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-gray-900">
                      {planDetails?.name} Plan
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                      ${planDetails?.price}/mo
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Billed monthly</p>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-gray-900 mb-3">What's Included:</h3>
                  {planDetails?.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900 font-medium">${planDetails?.price}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{trialDays}-day free trial</span>
                    <span className="text-green-600 font-medium">-${planDetails?.price}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold text-gray-900">Due Today</span>
                    <span className="text-2xl font-bold text-gray-900">$0.00</span>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium mb-1">
                    🎉 Your trial starts today!
                  </p>
                  <p className="text-xs text-green-700">
                    First charge on {new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>• Cancel anytime during your trial</p>
                  <p>• No charge until trial ends</p>
                  <p>• 30-day money-back guarantee</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Checkout;
